import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateGridActionIconSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const defaultRuleForm = {
  rule_name: '',
  condition_type: '',
  threshold_hours: '',
  multiplier: '',
  applies_to: 'NON_EXEMPT_HOURLY',
  effective_from_date: '',
  effective_to_date: '',
  status: 'ACTIVE',
};

const mapRuleRow = (item) => ({
  id: item.state_overtime_rule_id || item.stateOvertimeRuleId,
  rule_name: item.rule_name || item.ruleName,
  condition_type: item.condition_type || item.conditionType,
  threshold_hours: item.threshold_hours ?? item.thresholdHours,
  multiplier: item.multiplier,
  applies_to: item.applies_to || item.appliesTo,
  effective_from_date: item.effective_from_date || item.effectiveFromDate || '',
  effective_to_date: item.effective_to_date || item.effectiveToDate || '',
  status: item.status || 'ACTIVE',
  raw: item,
});

const WorkforceOtStatePolicyDrawer = ({ open, onClose, stateRow }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OT_STATE);
  const tenantId = getTenantId();
  const stateId = stateRow?.id;

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [otOptions, setOtOptions] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [ruleForm, setRuleForm] = useState(defaultRuleForm);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expanded, setExpanded] = useState('rules');
  const [ruleTypeFocused, setRuleTypeFocused] = useState(false);

  const selectedRule = useMemo(
    () => rules.find((row) => row.id === selectionModel[0]),
    [rules, selectionModel]
  );

  const loadOtOptions = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await apiFetch(`conductor/lookup-list?list_code=OVERTIME_RULES&tenant_id=${encodeURIComponent(tenantId)}`);
      const items = data?.list_items || data?.listItems || [];
      setOtOptions(Array.isArray(items) ? items : []);
    } catch {
      setOtOptions([]);
    }
  }, [tenantId]);

  const loadRules = useCallback(async () => {
    if (!stateId) {
      setRules([]);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await apiFetch(`conductor/workforce-state/${stateId}/overtime-rules?all=true`);
      setRules((Array.isArray(data) ? data : []).map(mapRuleRow));
    } catch (err) {
      setRules([]);
      setError(err.message || 'Failed to load overtime rules');
    } finally {
      setLoading(false);
    }
  }, [stateId]);

  useEffect(() => {
    if (!open) return;
    setExpanded('rules');
    loadOtOptions();
    loadRules();
    setSelectionModel([]);
  }, [open, loadOtOptions, loadRules]);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const columns = useMemo(() => [
    {
      field: 'rule_name',
      headerName: 'Rule Name',
      flex: 1,
      minWidth: 150,
      resizable: true,
      valueFormatter: (params) => {
        const opt = otOptions.find((item) => item.code === params.row?.condition_type);
        return params.value || opt?.label || '';
      },
    },
    { field: 'condition_type', headerName: 'Type', width: 140, resizable: true },
    { field: 'threshold_hours', headerName: 'Threshold Hrs', width: 120, resizable: true },
    { field: 'multiplier', headerName: 'Multiplier', width: 110, resizable: true },
    { field: 'applies_to', headerName: 'Applies To', width: 150, resizable: true },
    { field: 'effective_from_date', headerName: 'Effective From', width: 130, resizable: true },
    { field: 'effective_to_date', headerName: 'Effective To', width: 120, resizable: true },
    { field: 'status', headerName: 'Status', width: 100, resizable: true },
  ], [otOptions]);

  const applyRuleTypeDefaults = (code) => {
    const opt = otOptions.find((item) => item.code === code);
    if (!opt) return;
    setRuleForm((prev) => ({
      ...prev,
      condition_type: code,
      rule_name: opt.label || prev.rule_name,
      threshold_hours: opt.defaultThreshold ?? opt.default_threshold ?? prev.threshold_hours,
      multiplier: opt.defaultMultiplier ?? opt.default_multiplier ?? prev.multiplier,
    }));
  };

  const openAddRule = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingRuleId(null);
    setRuleTypeFocused(false);
    setRuleForm({ ...defaultRuleForm, effective_from_date: new Date().toISOString().slice(0, 10) });
    setRuleDialogOpen(true);
  };

  const openEditRule = () => {
    if (!canEdit || !selectedRule) return;
    setMode('edit');
    setEditingRuleId(selectedRule.id);
    setRuleTypeFocused(false);
    setRuleForm({
      rule_name: selectedRule.rule_name || '',
      condition_type: selectedRule.condition_type || '',
      threshold_hours: selectedRule.threshold_hours ?? '',
      multiplier: selectedRule.multiplier ?? '',
      applies_to: selectedRule.applies_to || 'NON_EXEMPT_HOURLY',
      effective_from_date: selectedRule.effective_from_date || '',
      effective_to_date: selectedRule.effective_to_date || '',
      status: selectedRule.status || 'ACTIVE',
    });
    setRuleDialogOpen(true);
  };

  const buildRulePayload = () => ({
    rule_name: ruleForm.rule_name?.trim(),
    condition_type: ruleForm.condition_type,
    threshold_hours: Number(ruleForm.threshold_hours),
    multiplier: Number(ruleForm.multiplier),
    applies_to: ruleForm.applies_to || 'NON_EXEMPT_HOURLY',
    effective_from_date: ruleForm.effective_from_date || null,
    effective_to_date: ruleForm.effective_to_date || null,
    status: ruleForm.status || 'ACTIVE',
  });

  const saveRule = async () => {
    if (!stateId) return;
    if (!ruleForm.condition_type) {
      setError('Please select a rule type.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const payload = buildRulePayload();
      if (mode === 'add') {
        await apiFetch(`conductor/workforce-state/${stateId}/overtime-rules`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`conductor/workforce-state/${stateId}/overtime-rules/${editingRuleId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      setRuleDialogOpen(false);
      setSelectionModel([]);
      loadRules();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRule = async () => {
    if (!stateId || !selectedRule) return;
    try {
      setDeleteLoading(true);
      setError('');
      await apiFetch(`conductor/workforce-state/${stateId}/overtime-rules/${selectedRule.id}`, {
        method: 'DELETE',
      });
      setDeleteOpen(false);
      setSelectionModel([]);
      loadRules();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const policyName = stateRow?.defaultPolicyName
    || (stateRow?.stateName ? `${stateRow.stateName} Standard` : 'Overtime Policy');

  const labelShrinkProps = (hasValue, focused) => ({
    shrink: hasValue || focused,
    sx: (hasValue || focused) ? { bgcolor: 'background.paper', px: 0.5 } : undefined,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 760 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: ht.drawerBg,
        },
      }}
    >
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
            {stateRow?.stateName || 'State'}
          </Typography>
          <Typography variant="body2" sx={{ color: ht.secondaryTab }}>
            {stateRow?.stateCode ? `${stateRow.stateCode} — ${policyName}` : 'Overtime Policy'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
        <Accordion
          expanded={expanded === 'rules'}
          onChange={handleAccordionChange('rules')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Overtime Policy Rules</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ ...harmonateDrawerAccordionDetailsSx, height: 420, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5, gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Add Rule">
                  <IconButton size="small" onClick={openAddRule} disabled={!canAdd} sx={harmonateGridActionIconSx}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Rule">
                  <span>
                    <IconButton size="small" disabled={!selectedRule || !canEdit} onClick={openEditRule} sx={harmonateGridActionIconSx}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete Rule">
                  <span>
                    <IconButton size="small" disabled={!selectedRule || !canDelete} onClick={() => setDeleteOpen(true)} sx={harmonateGridActionIconSx}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {error ? (
                <Typography variant="body2" sx={{ color: ht.red }}>
                  {error}
                </Typography>
              ) : null}

              <Paper sx={{ flex: 1, borderRadius: 0, overflow: 'hidden', boxShadow: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <DataGridPro
                  rows={rules}
                  columns={columns}
                  loading={loading}
                  rowHeight={40}
                  pagination
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  rowSelectionModel={selectionModel}
                  onRowSelectionModelChange={setSelectionModel}
                  disableMultipleRowSelection
                  sx={{ ...harmonateDataGridSx, border: 'none', flex: 1, minHeight: 0 }}
                />
              </Paper>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Overtime Rule' : 'Edit Overtime Rule'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            select
            fullWidth
            size="small"
            required
            label="Rule Type"
            value={ruleForm.condition_type}
            onChange={(e) => applyRuleTypeDefaults(e.target.value)}
            onFocus={() => setRuleTypeFocused(true)}
            onBlur={() => setRuleTypeFocused(false)}
            InputLabelProps={{ ...harmonateInputLabelProps, ...labelShrinkProps(Boolean(ruleForm.condition_type), ruleTypeFocused) }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: harmonateSelectMenuProps,
              renderValue: (selected) => {
                if (!selected) return '';
                const opt = otOptions.find((item) => item.code === selected);
                return opt?.label || selected;
              },
            }}
          >
            <MenuItem value="">
              <em>Select rule type</em>
            </MenuItem>
            {otOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>{opt.label || opt.code}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            required
            label="Rule Name"
            value={ruleForm.rule_name}
            onChange={(e) => setRuleForm((p) => ({ ...p, rule_name: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Threshold Hours"
              value={ruleForm.threshold_hours}
              onChange={(e) => setRuleForm((p) => ({ ...p, threshold_hours: e.target.value }))}
            />
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Multiplier"
              value={ruleForm.multiplier}
              onChange={(e) => setRuleForm((p) => ({ ...p, multiplier: e.target.value }))}
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            select
            label="Applies To"
            value={ruleForm.applies_to}
            onChange={(e) => setRuleForm((p) => ({ ...p, applies_to: e.target.value }))}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="NON_EXEMPT_HOURLY">NON_EXEMPT_HOURLY</MenuItem>
            <MenuItem value="ALL_HOURLY">ALL_HOURLY</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="date"
              label="Effective From"
              InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
              value={ruleForm.effective_from_date}
              onChange={(e) => setRuleForm((p) => ({ ...p, effective_from_date: e.target.value }))}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Effective To"
              InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
              value={ruleForm.effective_to_date}
              onChange={(e) => setRuleForm((p) => ({ ...p, effective_to_date: e.target.value }))}
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            select
            label="Status"
            value={ruleForm.status}
            onChange={(e) => setRuleForm((p) => ({ ...p, status: e.target.value }))}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRuleDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveRule}
            disabled={submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete Rule</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>Delete overtime rule &quot;{selectedRule?.rule_name || 'selected'}&quot;?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={deleteRule} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default WorkforceOtStatePolicyDrawer;
